import React, { useEffect, useMemo, useState } from 'react';
import { Download, Eye, Scissors, Send, Trash2 } from 'lucide-react';
import { Button, Card, Input } from '../../../components/ui';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';
import { generarPdfListaBlob } from '../../../utils/pdfListas';
import { shareImageNative } from '../../../utils/nativeShare';
import { openWhatsApp } from '../../../utils/whatsappShare';

interface SorteoItem {
  id: string;
  tipo_id: string;
  nombre: string;
  fecha: string;
  estado: string;
  jugadas_hoy?: number;
}

interface RecorteItem {
  id: string;
  tipo_recorte: 'NUMERO' | 'GLOBAL';
  numero: string | null;
  tipo_juego: string | null;
  monto_recorte: string | null;
  porcentaje_recorte: string | null;
  modo_global: string | null;
  motivo: string | null;
}

interface PreviewItem {
  id: number;
  numero: string;
  tipo_juego: string | null;
  monto_original: string;
  recorte_aplicado: string;
  monto_final_banquero: string;
  recortes_aplicados: string[];
}

interface PreviewRecorteLista {
  sorteo_id: string;
  sorteo_nombre: string;
  tipo_id: string;
  fecha: string;
  advertencia: string;
  total_original: string;
  total_recortado: string;
  total_rebajado: string;
  recortes: RecorteItem[];
  items: PreviewItem[];
}

const fmtMonto = (value: unknown): string => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.trunc(n).toLocaleString('es-CR');
};

const itemKey = (item: PreviewItem) => `${item.numero}||${item.tipo_juego || ''}`;

const buildPdfRows = (tipo: string, items: PreviewItem[]) => {
  if (tipo === 'NORMAL' || tipo === 'FECHAS') {
    return items.map((item) => ({ numero: item.numero, monto_total: Number(item.monto_final_banquero) || 0 }));
  }
  if (tipo === 'REVENTADO') {
    const map = new Map<string, any>();
    for (const item of items) {
      const row = map.get(item.numero) || { numero: item.numero, vendido_rev1: 0, vendido_rev2: 0 };
      if ((item.tipo_juego || '').toUpperCase() === 'REV2') row.vendido_rev2 += Number(item.monto_final_banquero) || 0;
      else row.vendido_rev1 += Number(item.monto_final_banquero) || 0;
      map.set(item.numero, row);
    }
    return Array.from(map.values());
  }
  if (tipo === 'MONAZOS') {
    return items.map((item) => ({ numero: item.numero, tipo: item.tipo_juego || '', vendido: Number(item.monto_final_banquero) || 0 }));
  }
  if (tipo === 'PARLEY') {
    return items.map((item) => ({ num1: item.numero, num2: item.tipo_juego || '', vendido: Number(item.monto_final_banquero) || 0 }));
  }
  return items.map((item) => ({ numero: item.numero, monto_total: Number(item.monto_final_banquero) || 0 }));
};

export const RecorteListaPage: React.FC = () => {
  const [fechaFiltro, setFechaFiltro] = useState(fechaLocalYMD);
  const [sorteos, setSorteos] = useState<SorteoItem[]>([]);
  const [selectedSorteoId, setSelectedSorteoId] = useState('');
  const [preview, setPreview] = useState<PreviewRecorteLista | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedItemKey, setSelectedItemKey] = useState('');
  const [montoNumero, setMontoNumero] = useState('');
  const [porcentajeGlobal, setPorcentajeGlobal] = useState('');
  const [motivo, setMotivo] = useState('');
  const [mensaje, setMensaje] = useState('');

  const selectedSorteo = useMemo(
    () => sorteos.find((sorteo) => sorteo.id === selectedSorteoId) || null,
    [sorteos, selectedSorteoId],
  );

  const selectedItem = useMemo(
    () => preview?.items.find((item) => itemKey(item) === selectedItemKey) || null,
    [preview?.items, selectedItemKey],
  );

  const cargarSorteos = () => {
    fetchAPI<SorteoItem[]>(`/sorteos?fecha=${fechaFiltro}`)
      .then((data) => {
        setSorteos(data || []);
        setSelectedSorteoId((prev) => {
          if (!data?.length) return '';
          if (data.some((sorteo) => sorteo.id === prev)) return prev;
          const conVentas = [...data].sort((a, b) => (b.jugadas_hoy ?? 0) - (a.jugadas_hoy ?? 0));
          return (conVentas[0]?.jugadas_hoy ?? 0) > 0 ? conVentas[0].id : data[0].id;
        });
      })
      .catch((err) => {
        console.error('Error cargando sorteos:', err);
        setSorteos([]);
      });
  };

  const cargarPreview = () => {
    if (!selectedSorteoId) return;
    setLoading(true);
    fetchAPI<PreviewRecorteLista>(`/lista-recortes/sorteos/${selectedSorteoId}/preview?fecha=${fechaFiltro}`)
      .then((data) => {
        setPreview(data);
        setSelectedItemKey((prev) => {
          if (data.items.some((item) => itemKey(item) === prev)) return prev;
          return data.items[0] ? itemKey(data.items[0]) : '';
        });
      })
      .catch((err) => {
        console.error('Error cargando recorte de lista:', err);
        setPreview(null);
        setMensaje(err?.message || 'Error cargando vista previa');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    cargarSorteos();
  }, [fechaFiltro]);

  useEffect(() => {
    cargarPreview();
  }, [selectedSorteoId, fechaFiltro]);

  const aplicarRecorteNumero = async () => {
    if (!selectedSorteoId || !selectedItem || saving) return;
    setSaving(true);
    setMensaje('');
    try {
      await fetchAPI('/lista-recortes', {
        method: 'POST',
        body: JSON.stringify({
          sorteo_id: selectedSorteoId,
          tipo_recorte: 'NUMERO',
          numero: selectedItem.numero,
          tipo_juego: selectedItem.tipo_juego,
          monto_recorte: Number(montoNumero),
          motivo: motivo || null,
        }),
      });
      setMontoNumero('');
      setMotivo('');
      setMensaje('Recorte por número aplicado.');
      cargarPreview();
    } catch (err: any) {
      setMensaje(err?.message || 'Error aplicando recorte');
    } finally {
      setSaving(false);
    }
  };

  const aplicarRecorteGlobal = async () => {
    if (!selectedSorteoId || saving) return;
    setSaving(true);
    setMensaje('');
    try {
      await fetchAPI('/lista-recortes', {
        method: 'POST',
        body: JSON.stringify({
          sorteo_id: selectedSorteoId,
          tipo_recorte: 'GLOBAL',
          modo_global: 'PORCENTAJE',
          porcentaje_recorte: Number(porcentajeGlobal),
          motivo: motivo || null,
        }),
      });
      setPorcentajeGlobal('');
      setMotivo('');
      setMensaje('Recorte global aplicado.');
      cargarPreview();
    } catch (err: any) {
      setMensaje(err?.message || 'Error aplicando recorte global');
    } finally {
      setSaving(false);
    }
  };

  const desactivarRecorte = async (recorteId: string) => {
    if (saving) return;
    setSaving(true);
    setMensaje('');
    try {
      await fetchAPI(`/lista-recortes/${recorteId}/desactivar`, { method: 'PATCH' });
      setMensaje('Recorte eliminado de la vista recortada.');
      cargarPreview();
    } catch (err: any) {
      setMensaje(err?.message || 'Error eliminando recorte');
    } finally {
      setSaving(false);
    }
  };

  const generarPdfRecortado = async (compartir = false) => {
    if (!preview || !selectedSorteo) return;
    let comisionNoGanadaPorRecorte: number | undefined;
    try {
      const params = new URLSearchParams({ fecha: fechaFiltro, sorteo_id: selectedSorteo.id });
      const reporte = await fetchAPI<any>(`/reportes/por-sorteo?${params.toString()}`);
      const sorteoReporte = reporte?.sorteos?.[0];
      if (sorteoReporte?.comision_no_ganada_por_recorte !== undefined) {
        comisionNoGanadaPorRecorte = Number(sorteoReporte.comision_no_ganada_por_recorte) || 0;
      }
    } catch (err) {
      console.error('Error cargando comisión de recorte para PDF:', err);
    }
    const blob = await generarPdfListaBlob({
      sorteo: selectedSorteo,
      fecha: fechaFiltro,
      paqueteNombre: null,
      localNombre: null,
      tipo: selectedSorteo.tipo_id || 'NORMAL',
      rows: buildPdfRows(selectedSorteo.tipo_id || 'NORMAL', preview.items),
      recorteResumen: {
        totalOriginal: Number(preview.total_original) || 0,
        totalRecortado: Number(preview.total_recortado) || 0,
        totalRebajado: Number(preview.total_rebajado) || 0,
        comisionNoGanadaPorRecorte,
      },
    });
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const safeName = selectedSorteo.nombre.replace(/[^a-zA-Z0-9_-]/g, '_');
    const fileName = `lista-recortada-${safeName}-${fechaFiltro}-${ts}.pdf`;
    const texto = `Lista recortada para banquero - ${selectedSorteo.nombre} - ${fechaFiltro}`;
    if (compartir) {
      const shared = await shareImageNative(blob, fileName, texto);
      if (!shared) openWhatsApp(texto);
      return;
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Recorte de Lista</h1>
          <p className="text-sm text-gray-500 mt-1">Vista derivada para preparar una lista recortada para banquero.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" leftIcon={<Eye size={18} />} onClick={cargarPreview} disabled={!selectedSorteoId || loading}>Vista previa lista para banquero</Button>
          <Button variant="secondary" leftIcon={<Download size={18} />} onClick={() => generarPdfRecortado(false)} disabled={!preview?.items.length}>PDF recortado</Button>
          <Button variant="secondary" leftIcon={<Send size={18} />} onClick={() => generarPdfRecortado(true)} disabled={!preview?.items.length}>WhatsApp</Button>
        </div>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
        LISTA RECORTADA PARA BANQUERO. LO RECORTADO QUEDA FUERA DE COBERTURA.
      </div>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha operativa</label>
            <input
              type="date"
              value={fechaFiltro}
              onChange={(event) => setFechaFiltro(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 min-h-[44px]"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Sorteo</label>
            <select
              value={selectedSorteoId}
              onChange={(event) => setSelectedSorteoId(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 min-h-[44px]"
            >
              <option value="">Seleccione un sorteo</option>
              {sorteos.map((sorteo) => (
                <option key={sorteo.id} value={sorteo.id}>{sorteo.nombre} ({sorteo.tipo_id})</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-blue-100"><p className="text-sm text-gray-500">Total original</p><p className="text-2xl font-bold text-gray-900">{fmtMonto(preview?.total_original)}</p></Card>
        <Card className="border-red-100"><p className="text-sm text-gray-500">Total rebajado</p><p className="text-2xl font-bold text-red-700">{fmtMonto(preview?.total_rebajado)}</p></Card>
        <Card className="border-emerald-100"><p className="text-sm text-gray-500">Total para banquero</p><p className="text-2xl font-bold text-emerald-700">{fmtMonto(preview?.total_recortado)}</p></Card>
      </div>

      <Card>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="space-y-3">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2"><Scissors size={18} /> Aplicar recorte por número</h2>
            <select
              value={selectedItemKey}
              onChange={(event) => setSelectedItemKey(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 min-h-[44px]"
            >
              <option value="">Seleccione una fila</option>
              {(preview?.items || []).map((item) => (
                <option key={itemKey(item)} value={itemKey(item)}>
                  {item.numero}{item.tipo_juego ? ` · ${item.tipo_juego}` : ''} · {fmtMonto(item.monto_original)}
                </option>
              ))}
            </select>
            <Input label="Monto a rebajar" type="number" min="0" value={montoNumero} onChange={(event) => setMontoNumero(event.target.value)} />
            <Button onClick={aplicarRecorteNumero} disabled={!selectedItem || !montoNumero || saving}>Aplicar recorte por número</Button>
          </div>
          <div className="space-y-3">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2"><Scissors size={18} /> Aplicar recorte global</h2>
            <Input label="Porcentaje global" type="number" min="0" max="100" step="0.01" value={porcentajeGlobal} onChange={(event) => setPorcentajeGlobal(event.target.value)} />
            <Input label="Motivo" value={motivo} onChange={(event) => setMotivo(event.target.value)} />
            <Button onClick={aplicarRecorteGlobal} disabled={!porcentajeGlobal || saving}>Aplicar recorte global</Button>
          </div>
        </div>
        {mensaje && <p className="mt-4 text-sm font-medium text-gray-700">{mensaje}</p>}
      </Card>

      <Card padding="none">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Lista recortada para banquero</h2>
          {loading && <span className="text-sm text-gray-500">Cargando...</span>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3">Número</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3 text-right">Monto original</th>
                <th className="px-4 py-3 text-right">Recorte</th>
                <th className="px-4 py-3 text-right">Monto final para banquero</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(preview?.items || []).map((item) => (
                <tr key={itemKey(item)}>
                  <td className="px-4 py-3 font-semibold text-gray-900">{item.numero}</td>
                  <td className="px-4 py-3 text-gray-600">{item.tipo_juego || 'GENERAL'}</td>
                  <td className="px-4 py-3 text-right">{fmtMonto(item.monto_original)}</td>
                  <td className="px-4 py-3 text-right text-red-700 font-semibold">{fmtMonto(item.recorte_aplicado)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-semibold">{fmtMonto(item.monto_final_banquero)}</td>
                </tr>
              ))}
              {!preview?.items.length && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">No hay jugadas activas para este sorteo.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Recortes activos</h2>
        <div className="space-y-2">
          {(preview?.recortes || []).map((recorte) => (
            <div key={recorte.id} className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between rounded-lg border border-gray-200 px-3 py-2">
              <div className="text-sm text-gray-700">
                <span className="font-semibold">{recorte.tipo_recorte}</span>
                {recorte.numero ? ` · ${recorte.numero}` : ''}
                {recorte.tipo_juego ? ` · ${recorte.tipo_juego}` : ''}
                {recorte.porcentaje_recorte ? ` · ${recorte.porcentaje_recorte}%` : ''}
                {recorte.monto_recorte ? ` · ${fmtMonto(recorte.monto_recorte)}` : ''}
                {recorte.motivo ? ` · ${recorte.motivo}` : ''}
              </div>
              <Button size="sm" variant="danger" leftIcon={<Trash2 size={16} />} onClick={() => desactivarRecorte(recorte.id)} disabled={saving}>Eliminar recorte</Button>
            </div>
          ))}
          {!preview?.recortes.length && <p className="text-sm text-gray-500">No hay recortes activos.</p>}
        </div>
      </Card>
    </div>
  );
};

export default RecorteListaPage;
